import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetExchangeRateQuery,
  useUpdateExchangeRateMutation,
  useGetAllProfitMarginsQuery,
  useUpdateServiceProfitMarginMutation,
  ServiceName,
  useGetCashbackSettingsQuery,
  useUpdateCashbackSettingsMutation,
  useToggleCashbackSystemMutation,
  CashbackServiceName,
  useGetSystemWebhookSecretQuery,
  useUpdateSystemWebhookSecretMutation,
  useGetServerLabelsQuery,
} from '../../store/api/adminApi';
import {
  flattenServerLabelGroups,
  resolveServiceKeyLabel,
  resolveServerLabel,
  type ServerCatalogId,
} from '../../utils/serverDisplayNames';
import { serviceAllowedByPlan, useTenantPlanFeatures } from '../../hooks/useTenantPlanFeatures';
import { IS_TENANT_MODE } from '../../config/tenantMode';

/** Only these margin fields are shown/editable for merchants (template + all tenants). */
const MERCHANT_MARGIN_FIELDS = new Set([
  'profitMargin',
  'profitMarginTemp',
  'profitMarginPrem',
  'profitMarginBase',
  'profitMarginDedicated',
  'profitMarginShared',
  'profitMarginPremium',
  'profitMarginMonthly',
  'profitMargin5',
  'profitMargin10',
  'profitMargin30',
]);

type MarginCardDef = {
  cardId: string;
  serviceName: ServiceName;
  catalogId: ServerCatalogId;
  fields: string[];
  group: 'temp' | 'perm' | 'proxy';
};

/** Rocket SaaS order: temp servers 1–4, permanent, proxies 1–4 */
const MARGIN_CARDS: MarginCardDef[] = [
  { cardId: 'temp-1', serviceName: 'smspool', catalogId: 'smspool', fields: ['profitMargin'], group: 'temp' },
  { cardId: 'temp-2', serviceName: 'durianRcs', catalogId: 'durianRcs', fields: ['profitMargin'], group: 'temp' },
  { cardId: 'temp-3', serviceName: 'textverified', catalogId: 'textverified_temp', fields: ['profitMargin'], group: 'temp' },
  {
    cardId: 'temp-4',
    serviceName: 'pvadeals',
    catalogId: 'pvadeals6',
    fields: ['profitMarginTemp', 'profitMarginPrem'],
    group: 'temp',
  },
  {
    cardId: 'perm-1',
    serviceName: 'textverified',
    catalogId: 'textverified_perm',
    fields: ['profitMarginMonthly'],
    group: 'perm',
  },
  {
    cardId: 'proxy-1',
    serviceName: 'proxycheap',
    catalogId: 'proxy_cheap',
    fields: ['profitMarginBase', 'profitMarginDedicated', 'profitMarginPremium'],
    group: 'proxy',
  },
  {
    cardId: 'proxy-2',
    serviceName: 'proxyline',
    catalogId: 'proxyline',
    fields: ['profitMarginBase', 'profitMarginDedicated', 'profitMarginShared'],
    group: 'proxy',
  },
  {
    cardId: 'proxy-3',
    serviceName: 'proxylte',
    catalogId: 'proxylte',
    fields: ['profitMargin', 'profitMarginMonthly', 'profitMargin5', 'profitMargin10', 'profitMargin30'],
    group: 'proxy',
  },
  { cardId: 'proxy-4', serviceName: 'proxy5g', catalogId: 'proxy5g', fields: ['profitMargin'], group: 'proxy' },
  { cardId: 'proxy-5', serviceName: 'proxygb', catalogId: 'proxygb', fields: ['profitMargin'], group: 'proxy' },
];

const MARGIN_GROUPS: { key: MarginCardDef['group']; title: string }[] = [
  { key: 'temp', title: 'أرقام مؤقتة' },
  { key: 'perm', title: 'أرقام دائمة' },
  { key: 'proxy', title: 'بروكسيات' },
];

export function AdminSystemConfigPage() {
  const { t } = useTranslation();
  const { hasNumbers, hasProxies } = useTenantPlanFeatures();
  const showCashback = !IS_TENANT_MODE;
  const [syriatelRate, setSyriatelRate] = useState('');
  const [shamRate, setShamRate] = useState('');
  const [isEditingExchangeRate, setIsEditingExchangeRate] = useState(false);

  // Exchange Rate
  const { data: exchangeRateData, isLoading: exchangeRateLoading } = useGetExchangeRateQuery();
  const [updateExchangeRate, { isLoading: isUpdatingExchangeRate }] = useUpdateExchangeRateMutation();

  // Profit Margins
  const { data: profitMarginsData, isLoading: profitMarginsLoading } = useGetAllProfitMarginsQuery();
  const [updateServiceProfitMargin, { isLoading: isUpdatingMargin }] = useUpdateServiceProfitMarginMutation();
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [marginForms, setMarginForms] = useState<Record<string, Record<string, string>>>({});

  // Cashback Settings (hidden for tenant merchants)
  const { data: cashbackData, isLoading: cashbackLoading } = useGetCashbackSettingsQuery(undefined, {
    skip: !showCashback,
  });
  const [updateCashbackSettings, { isLoading: isUpdatingCashback }] = useUpdateCashbackSettingsMutation();
  const [toggleCashbackSystem, { isLoading: isTogglingCashback }] = useToggleCashbackSystemMutation();

  const { data: serverLabelsData } = useGetServerLabelsQuery();
  const serverLabelRows = flattenServerLabelGroups(serverLabelsData?.data);

  const [isEditingCashback, setIsEditingCashback] = useState(false);

  // Webhook Secret (Rocket API only — hidden for tenant merchants)
  const showWebhookSecret = !IS_TENANT_MODE;
  const { data: webhookSecretData, isLoading: webhookSecretLoading } = useGetSystemWebhookSecretQuery(undefined, {
    skip: !showWebhookSecret,
  });
  const [updateWebhookSecret, { isLoading: isUpdatingWebhookSecret }] = useUpdateSystemWebhookSecretMutation();
  const [webhookSecretInput, setWebhookSecretInput] = useState('');
  const [isEditingWebhookSecret, setIsEditingWebhookSecret] = useState(false);

  const handleUpdateWebhookSecret = async () => {
    try {
      await updateWebhookSecret({ secret: webhookSecretInput }).unwrap();
      toast.success(webhookSecretInput
        ? 'Webhook secret configured successfully'
        : 'Webhook secret cleared'
      );
      setIsEditingWebhookSecret(false);
      setWebhookSecretInput('');
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || 'Failed to update webhook secret');
    }
  };

  // Initialize exchange rate form
  useEffect(() => {
    if (exchangeRateData?.data.rate && !syriatelRate && !isEditingExchangeRate) {
      setSyriatelRate(exchangeRateData.data.rate.syriatelCashExchangeRate.toString());
      setShamRate(exchangeRateData.data.rate.shamCashExchangeRate.toString());
    }
  }, [exchangeRateData, syriatelRate, isEditingExchangeRate]);

  const handleUpdateExchangeRate = async () => {
    const syriatel = parseFloat(syriatelRate);
    const sham = parseFloat(shamRate);
    if (isNaN(syriatel) || syriatel <= 0 || isNaN(sham) || sham <= 0) {
      toast.error(t('admin.systemConfig.exchangeRate.invalidRate'));
      return;
    }

    try {
      await updateExchangeRate({
        syriatelCashExchangeRate: syriatel,
        shamCashExchangeRate: sham,
      }).unwrap();
      toast.success(t('admin.systemConfig.exchangeRate.updateSuccess'));
      setIsEditingExchangeRate(false);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.exchangeRate.updateError')
      );
    }
  };
  const [cashbackForm, setCashbackForm] = useState({
    defaultPercentage: '',
    services: {} as Record<string, string>,
    proxycheap: {
      basic: '',
      dedicated: '',
      premium: '',
    },
  });

  const handleStartEditCard = (card: MarginCardDef, margins: Record<string, number>) => {
    // SAAS_FIXES_20261006_MARGINS_PCT_UI — edit as percent (10 = 10%), matching display
    setEditingCardId(card.cardId);
    const formData: Record<string, string> = {};
    card.fields.forEach((key) => {
      if (!MERCHANT_MARGIN_FIELDS.has(key)) return;
      const v = margins[key];
      formData[key] =
        v == null || !Number.isFinite(Number(v))
          ? ''
          : String(Number((Number(v) * 100).toFixed(4)));
    });
    setMarginForms((prev) => ({ ...prev, [card.cardId]: formData }));
  };

  const handleUpdateCardMargin = async (card: MarginCardDef) => {
    const formData = marginForms[card.cardId];
    if (!formData) return;

    const updateData: Record<string, number> = {};
    let hasValidData = false;

    // SAAS_FIXES_20261006_MARGINS_PCT_UI — convert percent input → decimal for API (0 allowed)
    Object.entries(formData).forEach(([key, value]) => {
      if (!card.fields.includes(key) || !MERCHANT_MARGIN_FIELDS.has(key)) return;
      const trimmed = String(value ?? '').trim();
      if (trimmed === '') return;
      const pct = parseFloat(trimmed);
      if (!Number.isNaN(pct) && pct >= 0) {
        updateData[key] = Math.round((pct / 100) * 1e6) / 1e6;
        hasValidData = true;
      }
    });

    if (!hasValidData) {
      toast.error(t('admin.systemConfig.profitMargins.invalidMargin'));
      return;
    }

    try {
      await updateServiceProfitMargin({
        serviceName: card.serviceName,
        data: updateData as Parameters<typeof updateServiceProfitMargin>[0]['data'],
      }).unwrap();
      toast.success(t('admin.systemConfig.profitMargins.updateSuccess'));
      setEditingCardId(null);
      setMarginForms((prev) => {
        const newForms = { ...prev };
        delete newForms[card.cardId];
        return newForms;
      });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.profitMargins.updateError')
      );
    }
  };

  const handleStartEditCashback = () => {
    if (!cashbackData?.data.settings) return;
    const settings = cashbackData.data.settings;
    setCashbackForm({
      defaultPercentage: (settings.defaultPercentage * 100).toFixed(2),
      services: {
        smspool: settings.services.smspool ? (settings.services.smspool * 100).toFixed(2) : '',
        pvadeals: settings.services.pvadeals ? (settings.services.pvadeals * 100).toFixed(2) : '',
        durianRcs: settings.services.durianRcs ? (settings.services.durianRcs * 100).toFixed(2) : '',
        proxyline: settings.services.proxyline ? (settings.services.proxyline * 100).toFixed(2) : '',
        warehouse: settings.services.warehouse ? (settings.services.warehouse * 100).toFixed(2) : '',
      },
      proxycheap: {
        basic: settings.services.proxycheap?.basic ? (settings.services.proxycheap.basic * 100).toFixed(2) : '',
        dedicated: settings.services.proxycheap?.dedicated ? (settings.services.proxycheap.dedicated * 100).toFixed(2) : '',
        premium: settings.services.proxycheap?.premium ? (settings.services.proxycheap.premium * 100).toFixed(2) : '',
      },
    });
    setIsEditingCashback(true);
  };

  const handleUpdateCashback = async () => {
    const defaultPercentage = parseFloat(cashbackForm.defaultPercentage) / 100;
    if (isNaN(defaultPercentage) || defaultPercentage < 0 || defaultPercentage > 1) {
      toast.error(t('admin.systemConfig.cashback.invalidPercentage'));
      return;
    }

    const services: Record<string, number> = {};
    Object.entries(cashbackForm.services).forEach(([key, value]) => {
      if (value) {
        const numValue = parseFloat(value) / 100;
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
          // تقريب إلى 4 خانات عشرية لتجنب مشاكل الفاصلة العائمة
          services[key] = Math.round(numValue * 10000) / 10000;
        }
      }
    });

    const proxycheap: { basic?: number; dedicated?: number; premium?: number } = {};
    if (cashbackForm.proxycheap.basic) {
      const numValue = parseFloat(cashbackForm.proxycheap.basic) / 100;
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
        proxycheap.basic = Math.round(numValue * 10000) / 10000;
      }
    }
    if (cashbackForm.proxycheap.dedicated) {
      const numValue = parseFloat(cashbackForm.proxycheap.dedicated) / 100;
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
        proxycheap.dedicated = Math.round(numValue * 10000) / 10000;
      }
    }
    if (cashbackForm.proxycheap.premium) {
      const numValue = parseFloat(cashbackForm.proxycheap.premium) / 100;
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
        proxycheap.premium = Math.round(numValue * 10000) / 10000;
      }
    }

    if (Object.keys(proxycheap).length > 0) {
      services.proxycheap = proxycheap as any;
    }

    try {
      // تقريب النسبة الافتراضية أيضاً
      const roundedDefaultPercentage = Math.round(defaultPercentage * 10000) / 10000;
      
      await updateCashbackSettings({
        defaultPercentage: roundedDefaultPercentage,
        services: Object.keys(services).length > 0 ? services : undefined,
      }).unwrap();
      toast.success(t('admin.systemConfig.cashback.updateSuccess'));
      setIsEditingCashback(false);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.cashback.updateError')
      );
    }
  };

  const handleToggleCashback = async (isEnabled: boolean) => {
    try {
      await toggleCashbackSystem({ isEnabled }).unwrap();
      toast.success(
        isEnabled
          ? t('admin.systemConfig.cashback.enabledSuccess')
          : t('admin.systemConfig.cashback.disabledSuccess')
      );
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.cashback.toggleError')
      );
    }
  };

  const getServiceDisplayName = (serviceName: string): string =>
    resolveServiceKeyLabel(serviceName, serverLabelRows, 'ar');

  const getCashbackServiceDisplayName = (serviceName: CashbackServiceName | 'proxycheap'): string =>
    resolveServiceKeyLabel(serviceName, serverLabelRows, 'ar');

  const getMarginFieldLabel = (fieldName: string): string => {
    const labels: Record<string, string> = {
      profitMargin: t('admin.systemConfig.profitMargins.fields.profitMargin'),
      profitMarginTemp: t('admin.systemConfig.profitMargins.fields.profitMarginTemp'),
      profitMarginPrem: t('admin.systemConfig.profitMargins.fields.profitMarginPrem'),
      profitMarginBase: t('admin.systemConfig.profitMargins.fields.profitMarginBase'),
      profitMarginDedicated: t('admin.systemConfig.profitMargins.fields.profitMarginDedicated'),
      profitMarginShared: t('admin.systemConfig.profitMargins.fields.profitMarginShared'),
      profitMarginPremium: t('admin.systemConfig.profitMargins.fields.profitMarginPremium'),
      profitMarginVoice: 'هامش الاتصال',
      profitMarginMonthly: 'هامش شهري',
      profitMargin5: 'هامش دوران 5 دقائق',
      profitMargin10: 'هامش دوران 10 دقائق',
      profitMargin30: 'هامش دوران 30 دقيقة',
    };
    return labels[fieldName] || fieldName;
  };

  const cardAllowedByPlan = (card: MarginCardDef) => {
    if (card.group === 'proxy') return hasProxies;
    return hasNumbers;
  };

  if (exchangeRateLoading || profitMarginsLoading || (showCashback && cashbackLoading) || (showWebhookSecret && webhookSecretLoading)) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  const currentSyriatelRate = exchangeRateData?.data.rate.syriatelCashExchangeRate || 0;
  const currentShamRate = exchangeRateData?.data.rate.shamCashExchangeRate || 0;
  const profitMargins = profitMarginsData?.data.margins || {};
  const cashbackSettings = cashbackData?.data.settings;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.systemConfig.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.systemConfig.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit Margins Section */}
        <div className="card-glass rounded-2xl shadow-2xl p-6">
          <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
            {t('admin.systemConfig.profitMargins.title')}
          </h2>

          <div className="space-y-8 max-h-[600px] overflow-y-auto">
            {MARGIN_GROUPS.map((group) => {
              const cards = MARGIN_CARDS.filter(
                (c) => c.group === group.key && cardAllowedByPlan(c)
              );
              if (cards.length === 0) return null;
              return (
                <div key={group.key}>
                  <h3 className="mb-3 text-lg font-heading font-bold text-brand-accent/90 border-b border-neutral-dark pb-2">
                    {group.title}
                  </h3>
                  <div className="space-y-4">
                    {cards.map((card) => {
                      const serviceMargins =
                        (profitMargins[card.serviceName] as Record<string, number>) || {};
                      const visibleEntries = card.fields
                        .filter((f) => MERCHANT_MARGIN_FIELDS.has(f))
                        .map((f) => [f, serviceMargins[f] ?? 0] as const);
                      const isEditing = editingCardId === card.cardId;
                      const formData = marginForms[card.cardId] || {};
                      const title = resolveServerLabel(card.catalogId, serverLabelRows, 'ar');

                      return (
                        <div
                          key={card.cardId}
                          className="bg-neutral-dark/50 p-4 rounded-lg border border-neutral-dark"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="text-lg font-heading font-semibold text-neutral-light">
                              {title}
                            </h4>
                            {!isEditing && (
                              <button
                                type="button"
                                onClick={() => handleStartEditCard(card, serviceMargins)}
                                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                              >
                                {t('admin.systemConfig.profitMargins.edit')}
                              </button>
                            )}
                          </div>

                          {!isEditing ? (
                            <div className="space-y-2">
                              {visibleEntries.map(([field, value]) => (
                                <div key={field} className="flex justify-between items-center">
                                  <span className="text-sm text-neutral-medium font-body">
                                    {getMarginFieldLabel(field)}:
                                  </span>
                                  <span className="text-sm font-heading font-semibold text-neutral-light">
                                    {(Number(value) * 100).toFixed(2)}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {card.fields
                                .filter((field) => MERCHANT_MARGIN_FIELDS.has(field))
                                .map((field) => (
                                  <div key={field}>
                                    <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                                      {getMarginFieldLabel(field)}
                                    </label>
                                    <input
                                      type="number"
                                      step="any"
                                      value={formData[field] || ''}
                                      onChange={(e) => {
                                        setMarginForms((prev) => ({
                                          ...prev,
                                          [card.cardId]: {
                                            ...prev[card.cardId],
                                            [field]: e.target.value,
                                          },
                                        }));
                                      }}
                                      placeholder="مثال: 10 لـ 10%"
                                      min={0}
                                      className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                                    />
                                  </div>
                                ))}
                              <div className="flex gap-2 mt-4">
                                <button
                                  type="button"
                                  onClick={() => handleUpdateCardMargin(card)}
                                  disabled={isUpdatingMargin}
                                  className="flex-1 px-3 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                  {isUpdatingMargin ? (
                                    <>
                                      <ButtonSpinner size="xs" />
                                      <span>{t('common.loading')}</span>
                                    </>
                                  ) : (
                                    t('common.save')
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingCardId(null);
                                    setMarginForms((prev) => {
                                      const newForms = { ...prev };
                                      delete newForms[card.cardId];
                                      return newForms;
                                    });
                                  }}
                                  className="flex-1 px-3 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 text-sm"
                                >
                                  {t('common.cancel')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cashback Settings & Exchange Rate Section */}
        <div className="card-glass rounded-2xl shadow-2xl p-6">
          {/* Exchange Rate Section */}
          <div className={showCashback ? 'mb-8 pb-8 border-b border-neutral-dark' : ''}>
            <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.systemConfig.exchangeRate.title')}
            </h2>

            {!isEditingExchangeRate ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                      {t('admin.systemConfig.exchangeRate.syriatelRate')}
                    </label>
                    <p className="text-3xl font-heading font-bold text-brand-accent">
                      {currentSyriatelRate.toLocaleString()} SYP
                    </p>
                    <p className="text-sm text-neutral-medium font-body mt-1">
                      {t('admin.systemConfig.exchangeRate.perUSD')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                      {t('admin.systemConfig.exchangeRate.shamRate')}
                    </label>
                    <p className="text-3xl font-heading font-bold text-brand-accent">
                      {currentShamRate.toLocaleString()} SYP
                    </p>
                    <p className="text-sm text-neutral-medium font-body mt-1">
                      {t('admin.systemConfig.exchangeRate.perUSD')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsEditingExchangeRate(true);
                    setSyriatelRate(currentSyriatelRate.toString());
                    setShamRate(currentShamRate.toString());
                  }}
                  className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('admin.systemConfig.exchangeRate.edit')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                    {t('admin.systemConfig.exchangeRate.syriatelRate')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={syriatelRate}
                    onChange={(e) => setSyriatelRate(e.target.value)}
                    placeholder={t('admin.systemConfig.exchangeRate.placeholder')}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                    {t('admin.systemConfig.exchangeRate.shamRate')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={shamRate}
                    onChange={(e) => setShamRate(e.target.value)}
                    placeholder={t('admin.systemConfig.exchangeRate.placeholder')}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleUpdateExchangeRate}
                    disabled={isUpdatingExchangeRate || !syriatelRate || parseFloat(syriatelRate) <= 0 || !shamRate || parseFloat(shamRate) <= 0}
                    className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                  >
                    {isUpdatingExchangeRate ? (
                      <>
                        <ButtonSpinner size="xs" />
                        <span>{t('common.loading')}</span>
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingExchangeRate(false);
                      setSyriatelRate('');
                      setShamRate('');
                    }}
                    className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Cashback Settings Section */}
          {showCashback && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold text-brand-accent">
                {t('admin.systemConfig.cashback.title')}
              </h2>
              {cashbackSettings && (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cashbackSettings.isEnabled}
                    onChange={(e) => handleToggleCashback(e.target.checked)}
                    disabled={isTogglingCashback}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-dark peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-dark after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-accent"></div>
                </label>
              )}
            </div>

          {!isEditingCashback ? (
            <div className="space-y-4">
              {cashbackSettings && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                      {t('admin.systemConfig.cashback.defaultPercentage')}
                    </label>
                    <p className="text-2xl font-heading font-bold text-brand-accent">
                      {(cashbackSettings.defaultPercentage * 100).toFixed(2)}%
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-heading font-semibold text-neutral-light">
                      {t('admin.systemConfig.cashback.services')}
                    </h3>
                    {Object.entries(cashbackSettings.services)
                      .filter(([serviceName]) =>
                        serviceAllowedByPlan(serviceName, hasNumbers, hasProxies)
                      )
                      .map(([serviceName, value]) => {
                      if (serviceName === 'proxycheap' && typeof value === 'object') {
                        if (!hasProxies) return null;
                        return (
                          <div key={serviceName} className="bg-neutral-dark/50 p-3 rounded-lg">
                            <p className="text-sm font-heading font-semibold text-neutral-light mb-2">
                              {getCashbackServiceDisplayName('proxycheap')}
                            </p>
                            <div className="space-y-1">
                              {value.basic !== undefined && (
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-neutral-medium font-body">
                                    {t('admin.systemConfig.cashback.proxycheap.basic')}:
                                  </span>
                                  <span className="text-xs font-heading font-semibold text-neutral-light">
                                    {(value.basic * 100).toFixed(2)}%
                                  </span>
                                </div>
                              )}
                              {value.dedicated !== undefined && (
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-neutral-medium font-body">
                                    {t('admin.systemConfig.cashback.proxycheap.dedicated')}:
                                  </span>
                                  <span className="text-xs font-heading font-semibold text-neutral-light">
                                    {(value.dedicated * 100).toFixed(2)}%
                                  </span>
                                </div>
                              )}
                              {value.premium !== undefined && (
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-neutral-medium font-body">
                                    {t('admin.systemConfig.cashback.proxycheap.premium')}:
                                  </span>
                                  <span className="text-xs font-heading font-semibold text-neutral-light">
                                    {(value.premium * 100).toFixed(2)}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                      if (typeof value === 'number') {
                        return (
                          <div key={serviceName} className="flex justify-between items-center">
                            <span className="text-sm text-neutral-medium font-body">
                              {getCashbackServiceDisplayName(serviceName as CashbackServiceName)}:
                            </span>
                            <span className="text-sm font-heading font-semibold text-neutral-light">
                              {(value * 100).toFixed(2)}%
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </>
              )}
              <button
                onClick={handleStartEditCashback}
                className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('admin.systemConfig.cashback.edit')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.systemConfig.cashback.defaultPercentage')} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={cashbackForm.defaultPercentage}
                  onChange={(e) => setCashbackForm({ ...cashbackForm, defaultPercentage: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.systemConfig.cashback.percentageNote')}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-neutral-light mb-3">
                  {t('admin.systemConfig.cashback.services')}
                </h3>
                <div className="space-y-3">
                  {(['smspool', 'pvadeals', 'durianRcs', 'proxyline', 'warehouse'] as CashbackServiceName[])
                    .filter((serviceName) => serviceAllowedByPlan(serviceName, hasNumbers, hasProxies))
                    .map((serviceName) => (
                    <div key={serviceName}>
                      <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                        {getCashbackServiceDisplayName(serviceName)}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={cashbackForm.services[serviceName] || ''}
                        onChange={(e) =>
                          setCashbackForm({
                            ...cashbackForm,
                            services: { ...cashbackForm.services, [serviceName]: e.target.value },
                          })
                        }
                        placeholder="0.00"
                        className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                      />
                    </div>
                  ))}

                  {/* Proxy server 1 cashback tiers */}
                  {hasProxies && (
                  <div className="bg-neutral-dark/50 p-3 rounded-lg">
                    <p className="text-sm font-heading font-semibold text-neutral-light mb-2">
                      {getCashbackServiceDisplayName('proxycheap')}
                    </p>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-neutral-medium mb-1 font-body">
                          {t('admin.systemConfig.cashback.proxycheap.basic')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={cashbackForm.proxycheap.basic}
                          onChange={(e) =>
                            setCashbackForm({
                              ...cashbackForm,
                              proxycheap: { ...cashbackForm.proxycheap, basic: e.target.value },
                            })
                          }
                          placeholder="0.00"
                          className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-medium mb-1 font-body">
                          {t('admin.systemConfig.cashback.proxycheap.dedicated')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={cashbackForm.proxycheap.dedicated}
                          onChange={(e) =>
                            setCashbackForm({
                              ...cashbackForm,
                              proxycheap: { ...cashbackForm.proxycheap, dedicated: e.target.value },
                            })
                          }
                          placeholder="0.00"
                          className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-medium mb-1 font-body">
                          {t('admin.systemConfig.cashback.proxycheap.premium')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={cashbackForm.proxycheap.premium}
                          onChange={(e) =>
                            setCashbackForm({
                              ...cashbackForm,
                              proxycheap: { ...cashbackForm.proxycheap, premium: e.target.value },
                            })
                          }
                          placeholder="0.00"
                          className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleUpdateCashback}
                  disabled={isUpdatingCashback}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                  {isUpdatingCashback ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => setIsEditingCashback(false)}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
          </div>
          )}
        </div>
      </div>

      {/* Webhook Secret Section — not for tenant merchants */}
      {showWebhookSecret && (
      <div className="mt-6 card-glass rounded-2xl shadow-2xl p-6">
        <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
          Webhook Signature Secret
        </h2>

        {!isEditingWebhookSecret ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                Status
              </label>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${webhookSecretData?.data.configured ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-lg font-heading font-bold text-neutral-light">
                  {webhookSecretData?.data.configured ? 'Configured' : 'Not Configured'}
                </span>
              </div>
              <p className="text-sm text-neutral-medium font-body mt-2">
                {webhookSecretData?.data.configured
                  ? 'Incoming webhooks require a valid X-Rocket-Signature header. Bypass is disabled.'
                  : 'No secret configured. Webhook signature verification is bypassed (token auth only).'}
              </p>
            </div>
            <button
              onClick={() => {
                setIsEditingWebhookSecret(true);
                setWebhookSecretInput('');
              }}
              className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
            >
              {webhookSecretData?.data.configured ? 'Change Secret' : 'Set Secret'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                Secret Key <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                value={webhookSecretInput}
                onChange={(e) => setWebhookSecretInput(e.target.value)}
                placeholder="Enter webhook signing secret"
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono"
              />
              <p className="text-xs text-neutral-medium font-body mt-1">
                Leave empty and save to clear the secret and disable signature verification.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleUpdateWebhookSecret}
                disabled={isUpdatingWebhookSecret}
                className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isUpdatingWebhookSecret ? (
                  <>
                    <ButtonSpinner size="xs" />
                    <span>Saving...</span>
                  </>
                ) : (
                  'Save'
                )}
              </button>
              <button
                onClick={() => {
                  setIsEditingWebhookSecret(false);
                  setWebhookSecretInput('');
                }}
                className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
